import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { anbieterApi } from '../api';

const TABS = ['Übersicht','Verträge','Adressen','Kanäle','Kontakte','Kommunikation'];

export default function AnbieterDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState(0);
  const [kosten, setKosten] = useState(null);
  const [showForm, setShowForm] = useState(null);
  const [formData, setFormData] = useState({});

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const [d, k] = await Promise.all([anbieterApi.get(id), anbieterApi.kosten(id)]);
      setData(d); setKosten(k);
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleAddAdresse = async () => {
    try { await anbieterApi.addAdresse(id, formData); setShowForm(null); setFormData({}); load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleAddKanal = async () => {
    if (!formData.kanal_typ || !formData.wert) { alert('Kanaltyp und Wert sind erforderlich'); return; }
    try { await anbieterApi.addKanal(id, formData); setShowForm(null); setFormData({}); load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const handleDeleteKanal = async (kanId) => {
    if (!window.confirm('Kanal löschen?')) return;
    try { await anbieterApi.deleteKanal(kanId); load(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6366f1' }}>⏳ Lade…</div>;
  if (err) return <div style={{ padding: 20, color: '#f87171' }}>{err}</div>;
  if (!data) return null;

  return (
    <div style={{ padding: 20, color: '#f1f5f9', maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>🏢 {data.name}</h1>
            <span style={{ background: '#1e293b', border: '1px solid #334155', padding: '2px 8px', borderRadius: 4, fontSize: 12, color: '#94a3b8' }}>{data.status}</span>
          </div>
          {data.kategorie && <div style={{ color: '#64748b', fontSize: 13 }}>📂 {data.kategorie}</div>}
          {data.beschreibung && <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{data.beschreibung}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => navigate(`/anbieter/${id}/bearbeiten`)} className="btn-secondary">Bearbeiten</button>
          <button onClick={() => navigate('/anbieter')} className="btn-ghost">← Zurück</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, overflowX: 'auto', borderBottom: '1px solid #334155' }}>
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)} style={{
            background: tab === i ? '#818cf8' : 'transparent', color: tab === i ? '#fff' : '#94a3b8',
            border: 'none', padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderRadius: '6px 6px 0 0', fontWeight: tab === i ? 600 : 400
          }}>{t}</button>
        ))}
      </div>

      {/* Tab 0: Übersicht */}
      {tab === 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
          <Card title="Kontaktdaten">
            {data.kundendienst_telefon && <Row label="Kundendienst" value={data.kundendienst_telefon} />}
            {data.kundendienst_email && <Row label="E-Mail" value={data.kundendienst_email} />}
            {data.kuendigungs_email && <Row label="Kündigung E-Mail" value={data.kuendigungs_email} />}
            {data.webseite && <Row label="Webseite" value={<a href={data.webseite} target="_blank" rel="noreferrer" style={{ color: '#818cf8' }}>{data.webseite}</a>} />}
          </Card>
          {kosten && (
            <Card title="Kosten">
              <Row label="Monatskosten" value={`${kosten.monatskosten?.toFixed(2)} €`} />
              <Row label="Jahreskosten" value={`${kosten.jahreskosten?.toFixed(2)} €`} />
              <Row label="Verträge" value={kosten.vertraege} />
            </Card>
          )}
          <Card title="Statistiken">
            <Row label="Verträge" value={data.vertraege?.length || 0} />
            <Row label="Kontakte" value={data.kontakte?.length || 0} />
            <Row label="Adressen" value={data.adressen?.length || 0} />
            <Row label="Quelle" value={data.quelle} />
          </Card>
          {data.notizen && (
            <Card title="Notizen">
              <p style={{ color: '#94a3b8', fontSize: 13, whiteSpace: 'pre-wrap', margin: 0 }}>{data.notizen}</p>
            </Card>
          )}
        </div>
      )}

      {/* Tab 1: Verträge */}
      {tab === 1 && (
        <div>
          {(data.vertraege || []).length === 0 ? <Empty text="Keine Verträge zugeordnet." /> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {data.vertraege.map(v => (
                <div key={v.id} onClick={() => navigate(`/vertraege/${v.id}`)}
                  style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 500 }}>{v.titel}</span>
                  <span style={{ color: '#64748b', fontSize: 13 }}>{v.kosten ? `${v.kosten} €/${v.zahlungsintervall}` : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Adressen */}
      {tab === 2 && (
        <div>
          <button onClick={() => setShowForm('adresse')} className="btn-primary" style={{ marginBottom: 12 }}>+ Adresse hinzufügen</button>
          {showForm === 'adresse' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Neue Adresse</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {['adress_typ','firmenname','strasse','hausnummer','plz','ort','bundesland','land'].map(f => (
                  <input key={f} placeholder={f} value={formData[f] || ''} onChange={e => setFormData(p => ({ ...p, [f]: e.target.value }))} style={inputStyle} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleAddAdresse} className="btn-primary">Speichern</button>
                <button onClick={() => { setShowForm(null); setFormData({}); }} className="btn-secondary">Abbrechen</button>
              </div>
            </div>
          )}
          {(data.adressen || []).length === 0 ? <Empty text="Keine Adressen hinterlegt." /> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {data.adressen.map(a => (
                <div key={a.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px' }}>
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{a.adress_typ}</div>
                  {a.firmenname && <div style={{ color: '#94a3b8', fontSize: 13 }}>{a.firmenname}</div>}
                  <div style={{ color: '#94a3b8', fontSize: 13 }}>{[a.strasse, a.hausnummer].filter(Boolean).join(' ')}{a.strasse && ', '}{a.plz} {a.ort}</div>
                  {a.land && a.land !== 'Deutschland' && <div style={{ color: '#64748b', fontSize: 12 }}>{a.land}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Kanäle */}
      {tab === 3 && (
        <div>
          <button onClick={() => setShowForm('kanal')} className="btn-primary" style={{ marginBottom: 12 }}>+ Kanal hinzufügen</button>
          {showForm === 'kanal' && (
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <select value={formData.kanal_typ || ''} onChange={e => setFormData(p => ({...p, kanal_typ: e.target.value}))} style={inputStyle}>
                  <option value="">Kanaltyp wählen</option>
                  {['Email','Telefon','Fax','URL','Portal','Chat','sonstiges'].map(k => <option key={k} value={k}>{k}</option>)}
                </select>
                <input placeholder="Wert (E-Mail, Nummer…)" value={formData.wert || ''} onChange={e => setFormData(p => ({...p, wert: e.target.value}))} style={inputStyle} />
                <input placeholder="Bezeichnung (optional)" value={formData.bezeichnung || ''} onChange={e => setFormData(p => ({...p, bezeichnung: e.target.value}))} style={inputStyle} />
                <input placeholder="Zweck (optional)" value={formData.zweck || ''} onChange={e => setFormData(p => ({...p, zweck: e.target.value}))} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={handleAddKanal} className="btn-primary">Speichern</button>
                <button onClick={() => { setShowForm(null); setFormData({}); }} className="btn-secondary">Abbrechen</button>
              </div>
            </div>
          )}
          {(data.kanaele || []).length === 0 ? <Empty text="Keine Kanäle hinterlegt." /> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {data.kanaele.map(k => (
                <div key={k.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 500, marginRight: 8 }}>{k.kanal_typ}</span>
                    <span style={{ color: '#94a3b8', fontSize: 13 }}>{k.wert}</span>
                    {k.bezeichnung && <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>({k.bezeichnung})</span>}
                  </div>
                  <button onClick={() => handleDeleteKanal(k.id)} className="btn-danger">Löschen</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Kontakte */}
      {tab === 4 && (
        <div>
          <button onClick={() => navigate(`/kontakte/neu?anbieter_id=${id}`)} className="btn-primary" style={{ marginBottom: 12 }}>+ Kontakt hinzufügen</button>
          {(data.kontakte || []).length === 0 ? <Empty text="Keine Kontakte hinterlegt." /> : (
            <div style={{ display: 'grid', gap: 8 }}>
              {data.kontakte.map(k => (
                <div key={k.id} onClick={() => navigate(`/kontakte/${k.id}`)}
                  style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '12px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 500 }}>{[k.vorname, k.nachname].filter(Boolean).join(' ')}</span>
                    {k.ist_primaer ? <span style={{ marginLeft: 8, color: '#6366f1', fontSize: 11 }}>★ Primär</span> : null}
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{k.rolle} {k.abteilung ? `· ${k.abteilung}` : ''}</div>
                    {k.email && <div style={{ color: '#94a3b8', fontSize: 12 }}>{k.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab 5: Kommunikation */}
      {tab === 5 && (
        <div>
          <button onClick={() => navigate(`/kommunikation/neu?anbieter_id=${id}`)} className="btn-primary" style={{ marginBottom: 12 }}>+ Kommunikation erfassen</button>
          <Empty text="Kommunikation über die Kommunikationsseite verwalten." />
        </div>
      )}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 16 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #0f172a', fontSize: 13 }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ color: '#f1f5f9', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>{value || '—'}</span>
    </div>
  );
}

function Empty({ text }) {
  return <div style={{ textAlign: 'center', color: '#64748b', padding: 30, fontSize: 14 }}>{text}</div>;
}

const inputStyle = { background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 10px', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
